import { Modal, Button, Form, message } from 'antd';
import { useMemo, useState } from 'react';
import { useCreateVisita } from '../api/useVisitas';
import { useT } from '../../../locales';
import { VisitaForm } from './VisitaForm';
import type { ClienteRef } from '@aid/shared';

interface VisitaCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Cliente preseleccionado al abrir (p. ej. desde la worklist del cobrador). */
  preselectClienteId?: number;
  /** Compact client data from the cobrador worklist; avoids an admin lookup. */
  preselectedCliente?: ClienteRef;
}

export function VisitaCreateModal({
  open,
  onClose,
  onSuccess,
  preselectClienteId,
  preselectedCliente,
}: VisitaCreateModalProps) {
  const t = useT();
  const [form] = Form.useForm();
  const [locationBlocked, setLocationBlocked] = useState(false);
  const createVisita = useCreateVisita();

  const initialValues = useMemo(() => {
    const idCliente = preselectClienteId ?? preselectedCliente?.id;
    return idCliente != null ? { idCliente } : undefined;
  }, [preselectClienteId, preselectedCliente?.id]);

  const handleSubmit = async (values: Record<string, unknown>) => {
    try {
      await createVisita.mutateAsync(values as Parameters<typeof createVisita.mutateAsync>[0]);
      message.success(t('pages.visitas.createSuccess'));
      onClose();
      onSuccess?.();
    } catch {
      message.error(t('pages.visitas.createError'));
    }
  };

  return (
    <Modal
      title={t('pages.visitas.create')}
      open={open}
      onCancel={onClose}
      destroyOnHidden
      centered
      width={640}
      styles={{ body: { maxHeight: 'min(75vh, 675px)', overflowY: 'auto' } }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('common.cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={createVisita.isPending}
          disabled={locationBlocked}
          onClick={() => form.submit()}
        >
          {t('pages.visitas.create')}
        </Button>,
      ]}
    >
      <VisitaForm
        form={form}
        hideSubmit
        initialValues={initialValues}
        preselectedCliente={preselectedCliente}
        onSubmit={handleSubmit}
        loading={createVisita.isPending}
        onLocationBlockedChange={setLocationBlocked}
      />
    </Modal>
  );
}
